import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Save, Download, Target, Building2, User, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";

const PRESET = {
  company_name: "Europacific",
  company_description:
    "Europacific provides freight forwarding, customs brokerage, and supply chain solutions across the EU and Western Balkans. We specialise in road, sea, and intermodal transport with strong expertise in Adriatic and Balkan trade lanes, offering reliable logistics support for growing international businesses.",
  industries: "Ecommerce, Retail, Automotive, Industrial manufacturing, Food & beverage, FMCG, Pharma, Consumer goods",
  geographies: "Slovenia, Croatia, Serbia, Bosnia, North Macedonia, Montenegro, EU",
  company_size: "25+ employees / >EUR 10M revenue",
  decision_maker_titles: "Head of Logistics, Supply Chain Director, Operations Manager, Procurement Manager, Import/Export Manager, Transport Manager, CEO",
};

const splitCSV = (s) => s.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);
const joinCSV = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

export default function ICPTargetsPage() {
  const [form, setForm] = useState({ ...PRESET });
  const [savedIcp, setSavedIcp] = useState(null);
  const [savingIcp, setSavingIcp] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [batch, setBatch] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [genCount, setGenCount] = useState(15);
  const [regionFocus, setRegionFocus] = useState("");
  const [industryFocus, setIndustryFocus] = useState("");
  const [importing, setImporting] = useState(false);

  const loadIcp = async () => {
    try {
      const { data } = await api.get("/icp");
      if (data.icp) {
        setSavedIcp(data.icp);
        setForm({
          company_name: data.icp.company_name || "",
          company_description: data.icp.company_description || "",
          industries: joinCSV(data.icp.industries),
          geographies: joinCSV(data.icp.geographies),
          company_size: data.icp.company_size || "",
          decision_maker_titles: joinCSV(data.icp.decision_maker_titles),
        });
      }
    } catch { /* no icp yet */ }
  };
  useEffect(() => { loadIcp(); }, []);

  const saveIcp = async () => {
    setSavingIcp(true);
    try {
      const payload = {
        company_name: form.company_name,
        company_description: form.company_description,
        industries: splitCSV(form.industries),
        geographies: splitCSV(form.geographies),
        company_size: form.company_size,
        decision_maker_titles: splitCSV(form.decision_maker_titles),
      };
      const { data } = await api.post("/icp", payload);
      setSavedIcp(data.icp);
      toast.success("ICP saved");
    } catch { toast.error("Failed to save ICP"); }
    setSavingIcp(false);
  };

  const generate = async () => {
    if (!savedIcp) { toast.error("Save your ICP first"); return; }
    setGenerating(true);
    setBatch(null);
    setSelectedLeads(new Set());
    try {
      const { data } = await api.post("/icp/generate-targets", {
        count: genCount,
        region_focus: regionFocus || null,
        industry_focus: industryFocus || null,
      });
      const batchId = data.batch_id;
      if (!batchId) throw new Error("no batch id");
      // Poll the batch until generation finishes (avoids gateway timeout on the
      // slow LLM call — generation runs server-side in the background).
      const startedAt = Date.now();
      const poll = async () => {
        try {
          const { data: b } = await api.get(`/icp/batches/${batchId}`);
          if (b.status === "done") {
            setBatch({ ...b, batch_id: batchId });
            const allLeads = new Set();
            (b.targets || []).forEach((t) => (t.contacts || []).forEach((c) => allLeads.add(c.lead_id)));
            setSelectedLeads(allLeads);
            toast.success(`Generated ${b.count} target companies`);
            setGenerating(false);
            return;
          }
          if (b.status === "failed") {
            toast.error(b.error || "Generation failed — try a smaller count.");
            setGenerating(false);
            return;
          }
          if (Date.now() - startedAt > 180000) {
            toast.error("Generation timed out. Try a smaller count (e.g. 8-10).");
            setGenerating(false);
            return;
          }
          setTimeout(poll, 3000);
        } catch {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error(e?.response?.data?.detail?.message || "AI budget/run cap reached — raise the caps in AI Budget.");
      } else {
        toast.error(e?.response?.data?.detail || "Generation failed");
      }
      setGenerating(false);
    }
  };

  const toggleLead = (id) => {
    setSelectedLeads((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const importSelected = async () => {
    if (!batch || selectedLeads.size === 0) {
      toast.error("Select at least one contact to import");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/icp/import", {
        batch_id: batch.batch_id,
        selected_lead_ids: Array.from(selectedLeads),
      });
      toast.success(`Imported ${data.imported} prospects. View them in Prospects.`);
      setBatch(null);
      setSelectedLeads(new Set());
    } catch { toast.error("Import failed"); }
    setImporting(false);
  };

  return (
    <DashboardLayout
      eyebrow="ICP & Target List"
      title="Define your ICP. Generate real targets with AI."
      actions={
        <Button onClick={saveIcp} disabled={savingIcp} data-testid="icp-save-btn" variant="outline" className="rounded-sm">
          <Save className="w-4 h-4 mr-1.5" />{savingIcp ? "Saving..." : "Save ICP"}
        </Button>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6 mb-7">
        <div className="lg:col-span-2 data-card space-y-3">
          <div className="label-eyebrow flex items-center gap-2"><Target className="w-3.5 h-3.5" /> Ideal Customer Profile</div>
          <Field label="Your company" testid="icp-company-name">
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="icp-input-company-name" />
          </Field>
          <Field label="What you do (1-2 sentences — AI uses this to write accurate messages)">
            <textarea
              data-testid="icp-input-description"
              value={form.company_description}
              onChange={(e) => setForm({ ...form, company_description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Target industries (comma-separated)">
              <Input value={form.industries} onChange={(e) => setForm({ ...form, industries: e.target.value })} data-testid="icp-input-industries" />
            </Field>
            <Field label="Target geographies (comma-separated)">
              <Input value={form.geographies} onChange={(e) => setForm({ ...form, geographies: e.target.value })} data-testid="icp-input-geos" />
            </Field>
            <Field label="Company size band">
              <Input value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })} data-testid="icp-input-size" />
            </Field>
            <Field label="Decision-maker titles (comma-separated)">
              <Input value={form.decision_maker_titles} onChange={(e) => setForm({ ...form, decision_maker_titles: e.target.value })} data-testid="icp-input-titles" />
            </Field>
          </div>
          {savedIcp && (
            <p className="text-xs text-muted-foreground">
              Last updated {new Date(savedIcp.updated_at).toLocaleString()}
            </p>
          )}
        </div>

        <div className="data-card flex flex-col gap-3">
          <div className="label-eyebrow flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Generate target list</div>
          <p className="text-xs text-muted-foreground">
            AI (GPT-5.2) picks real-likely companies in your geos + industries and suggests 1-2 decision-makers per company. Curate, then import as prospects.
          </p>
          <Field label="How many companies?">
            <Input type="number" min={1} max={40} value={genCount} onChange={(e) => setGenCount(parseInt(e.target.value) || 15)} data-testid="icp-input-count" />
          </Field>
          <Field label="Region focus (optional override)">
            <Input value={regionFocus} onChange={(e) => setRegionFocus(e.target.value)} placeholder="e.g. Slovenia + Croatia only" data-testid="icp-input-region-focus" />
          </Field>
          <Field label="Industry focus (optional override)">
            <Input value={industryFocus} onChange={(e) => setIndustryFocus(e.target.value)} placeholder="e.g. Pharma + Food only" data-testid="icp-input-industry-focus" />
          </Field>
          <Button
            onClick={generate}
            disabled={generating || !savedIcp}
            data-testid="icp-generate-btn"
            className="mt-2 rounded-sm"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {generating ? "Generating with AI..." : "Generate target list"}
          </Button>
          {!savedIcp && (
            <p className="text-[0.65rem] text-amber-accent">Save your ICP first.</p>
          )}
        </div>
      </div>

      {batch && (
        <div data-testid="icp-batch-results">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Review &amp; curate</div>
              <h2 className="text-xl font-display font-semibold tracking-tightest">{batch.count} target companies generated</h2>
              <div className="text-xs text-muted-foreground">{selectedLeads.size} contacts selected for import</div>
            </div>
            <Button
              onClick={importSelected}
              disabled={importing || selectedLeads.size === 0}
              data-testid="icp-import-btn"
              className="rounded-sm"
            >
              <Download className="w-4 h-4 mr-1.5" />
              {importing ? "Importing..." : `Import ${selectedLeads.size} as prospects`}
            </Button>
          </div>

          <div className="space-y-3">
            {(batch.targets || []).map((t) => (
              <div key={t.lead_id} className="data-card" data-testid={`target-card-${t.lead_id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Building2 className="w-4 h-4 text-signal-500 flex-shrink-0" />
                      <span className="font-display font-semibold text-base">{t.company}</span>
                      <span className="signal-pill">{t.industry}</span>
                      {t.size_band && <span className="signal-pill">{t.size_band}</span>}
                      <span className="text-xs text-muted-foreground">{t.city}{t.country ? `, ${t.country}` : ""}</span>
                    </div>
                    {t.website_guess && (
                      <a href={t.website_guess} target="_blank" rel="noreferrer"
                         className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-1">
                        <Globe className="w-3 h-3" /> {t.website_guess.replace(/^https?:\/\//, "")} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                    {t.rationale && (
                      <div className="text-xs text-muted-foreground mt-1.5 italic">&quot;{t.rationale}&quot;</div>
                    )}
                    {Array.isArray(t.signals) && t.signals.length > 0 && (
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {t.signals.map((s) => <span key={s} className="signal-pill">{s.replace(/_/g, " ")}</span>)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-2 pl-6 border-l-2 border-signal-500/30">
                  {(t.contacts || []).map((c) => {
                    const sel = selectedLeads.has(c.lead_id);
                    return (
                      <label
                        key={c.lead_id}
                        className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                          sel ? "border-signal-500/40 bg-signal-500/5" : "border-border bg-card/30 hover:bg-muted/30"
                        }`}
                        data-testid={`target-contact-${c.lead_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={sel}
                          onChange={() => toggleLead(c.lead_id)}
                          data-testid={`target-contact-cb-${c.lead_id}`}
                          className="mt-1"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium text-sm truncate">{c.full_name}</span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{c.title}</div>
                          {c.email_pattern_guess && (
                            <div className="text-[0.65rem] font-mono text-muted-foreground truncate mt-0.5">{c.email_pattern_guess}</div>
                          )}
                          {c.linkedin_search_url && (
                            <a href={c.linkedin_search_url} target="_blank" rel="noreferrer"
                               onClick={(e) => e.stopPropagation()}
                               className="text-[0.65rem] text-signal-500 hover:underline inline-flex items-center gap-1 mt-0.5">
                              LinkedIn search <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!batch && savedIcp && (
        <div className="data-card text-center py-12 text-sm text-muted-foreground" data-testid="icp-empty-batch">
          Click <span className="font-mono">Generate target list</span> above to have GPT-5.2 build your prospect pipeline.
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label className="text-xs mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
